import type { TransportOption, TripStop } from 'tripPlanner/api/types'
import { getAirportsSearch } from 'api/flights'
import type { FlightSearchDrawerParams } from './flightSearchDrawerParams'
import { getFlightSearchDrawerParamsFromTransport } from './flightSearchDrawerParams'

export interface StopFlightSearchDefaults {
  flightSearchParams: FlightSearchDrawerParams
  existingFlights?: Array<TransportOption>
}

function isFlightTransportOption(option: TransportOption): boolean {
  return option.type?.toLowerCase() === 'flight'
}

function chooseAirport(airports: Array<App.AirportLocation>): App.AirportLocation | undefined {
  return airports.find(airport => airport.isMultiAirport === true && airport.airportCode.endsWith('ALL')) ??
    airports.find(airport => /^[A-Z]{3}$/.test(airport.airportCode)) ??
    airports[0]
}

function getPreviousTripStop(stops: Array<TripStop>, stop: TripStop): TripStop | undefined {
  const orderedStops = [...stops].sort((a, b) => a.stopSeq - b.stopSeq)
  const stopIndex = orderedStops.findIndex(candidate => candidate.id === stop.id)
  return stopIndex > 0 ? orderedStops[stopIndex - 1] : undefined
}

export function getAdjacentStopFlightSearchDefaults(stops: Array<TripStop>, stop: TripStop): StopFlightSearchDefaults | undefined {
  const previousStop = getPreviousTripStop(stops, stop)
  const transport = previousStop?.transports?.find(candidate => candidate.options.some(isFlightTransportOption))
  const option = transport?.options.find(isFlightTransportOption)
  if (!transport || !option) return undefined

  return {
    flightSearchParams: getFlightSearchDrawerParamsFromTransport(option, transport),
    existingFlights: transport.options,
  }
}

export function getStopFlightSearchDefaults(stops: Array<TripStop>, stop: TripStop): StopFlightSearchDefaults {
  const adjacentFlightDefaults = getAdjacentStopFlightSearchDefaults(stops, stop)
  if (adjacentFlightDefaults) return adjacentFlightDefaults

  const previousStop = getPreviousTripStop(stops, stop)

  return {
    flightSearchParams: {
      originAirportName: previousStop?.placeName,
      destinationAirportName: stop.placeName,
      departureDate: previousStop ? stop.startDate : undefined,
    },
  }
}

/** Resolves name-only stop defaults to valid airport records before opening the flight drawer. */
export async function resolveStopFlightSearchAirportDefaults(
  stops: Array<TripStop>,
  stop: TripStop,
  params: FlightSearchDrawerParams,
): Promise<FlightSearchDrawerParams> {
  const previousStop = getPreviousTripStop(stops, stop)
  const [originAirport, destinationAirport] = await Promise.all([
    params.originAirportCode || !previousStop?.placeName
      ? undefined
      : getAirportsSearch(previousStop.placeName).then(chooseAirport).catch(() => undefined),
    params.destinationAirportCode || !stop.placeName
      ? undefined
      : getAirportsSearch(stop.placeName).then(chooseAirport).catch(() => undefined),
  ])

  return {
    ...params,
    originAirportCode: params.originAirportCode ?? originAirport?.airportCode,
    originAirportName: params.originAirportName ?? originAirport?.cityAirportName ?? originAirport?.airportName,
    destinationAirportCode: params.destinationAirportCode ?? destinationAirport?.airportCode,
    destinationAirportName: params.destinationAirportName ?? destinationAirport?.cityAirportName ?? destinationAirport?.airportName,
  }
}
